import React, { useState, useEffect } from "react";
import api from "../api";
import { toast } from 'react-toastify';
import { FaFileDownload, FaInfoCircle } from "react-icons/fa"; 

const UploadBeneficiaryListPage = () => {
    const projectId = localStorage.getItem("selectedProjectId");
    const departmentId = localStorage.getItem("departmentId");

    const [files, setFiles] = useState([]);
    const [voucherPoints, setVoucherPoints] = useState("");
    const [validityStart, setValidityStart] = useState("");
    const [validityEnd, setValidityEnd] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    
    const [uploadResponseMsg, setUploadResponseMsg] = useState("");
    const [error, setError] = useState(""); 

    useEffect(() => {
        if (!projectId || !departmentId) {
            const missingInfoMessage = "Project or Department information is missing. Please log out and select a project again.";
            setError(missingInfoMessage);
            toast.error(missingInfoMessage);
        }
    }, [projectId, departmentId]);

    const handleFileChange = (e) => {
        setFiles(Array.from(e.target.files));
        setUploadResponseMsg("");
        setError("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setUploadResponseMsg("");

        if (!projectId || !departmentId) {
            const missingInfoMessage = "Cannot submit. Project or Department information is missing.";
            setError(missingInfoMessage);
            toast.error(missingInfoMessage);
            return;
        }

        if (files.length === 0) {
            const noFileMessage = "Please select at least one Excel file.";
            setError(noFileMessage);
            toast.error(noFileMessage);
            return;
        }

        if (!voucherPoints || !validityStart || !validityEnd) {
            const msg = "Please fill in Voucher Points and Validity Dates.";
            setError(msg);
            toast.error(msg);
            return;
        }

        if (validityStart && validityEnd && validityEnd < validityStart) {
            const dateErrorMsg = "Validity end date cannot be before the start date.";
            setError(dateErrorMsg);
            toast.error(dateErrorMsg);
            return;
        }

        const allowedTypes = [".xls", ".xlsx"];
        const invalidFiles = files.filter(
            (file) => !allowedTypes.some((ext) => file.name.endsWith(ext))
        );

        if (invalidFiles.length > 0) {
            const invalidFileMessage = "Only Excel files (.xls, .xlsx) are allowed.";
            setError(invalidFileMessage);
            toast.error(invalidFileMessage);
            return;
        }

        setIsLoading(true);

        const formData = new FormData();
        formData.append("department_id", departmentId);
        formData.append("voucher_points", voucherPoints);
        formData.append("validity_start", validityStart);
        formData.append("validity_end", validityEnd);

        files.forEach((file) => {
            formData.append("beneficiary_files", file);
        });

        try {
            const response = await api.post(
                `/vres/projects/${projectId}/beneficiaries/upload`,
                formData
            );

            // Backend returns: "Success part || Error Header :: 1. Error :: 2. Error"
            const backendMsg = response.data.message || `Successfully submitted.`;
            setUploadResponseMsg(backendMsg);
            
            toast.success("Upload processed.");

            setFiles([]);
            setVoucherPoints("");
            setValidityStart("");
            setValidityEnd("");
            const fileInput = document.getElementById("beneficiary_files");
            if (fileInput) fileInput.value = "";

        } catch (err) {
            console.error("Upload error:", err);
            let displayMessage = "An unexpected error occurred during the upload. Please try again.";

            if (err.response) {
                const backendData = err.response.data;
                let backendMessage = "";
                if (backendData && typeof backendData === 'object' && backendData.message) {
                    backendMessage = backendData.message;
                } else if (typeof backendData === 'string') {
                    backendMessage = backendData;
                }

                if (backendMessage) {
                    displayMessage = `Upload failed: ${backendMessage}`;
                } else if (err.response.status === 403) {
                    displayMessage = "Upload failed: You do not have permission.";
                } else {
                    displayMessage = `Upload failed: Request failed with status code ${err.response.status}.`;
                }
            } else if (err.message) {
                displayMessage = `Upload failed: ${err.message}`;
            }

            setError(displayMessage);
            toast.error(displayMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setFiles([]);
        setVoucherPoints("");
        setValidityStart("");
        setValidityEnd("");
        setUploadResponseMsg("");
        setError("");
        const fileInput = document.getElementById("beneficiary_files");
        if (fileInput) fileInput.value = "";
    };

    // --- Helper to Render the Split Message ---
    const renderStatusMessage = () => {
        if (!uploadResponseMsg) return null;

        // 1. Split Success and Error parts
        const parts = uploadResponseMsg.split(" || ");
        const successPart = parts[0];
        
        // 2. Parse Error List if it exists
        let errorHeader = null;
        let errorList = [];

        if (parts.length > 1) {
            // Backend sends: "Header :: 1. Item :: 2. Item"
            const errorParts = parts[1].split("::");
            errorHeader = errorParts[0]; // The text before the first ::
            errorList = errorParts.slice(1); // The actual numbered items
        }

        return (
            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px', border: '1px solid #dee2e6' }}>
                {/* Success Message in Green */}
                <div style={{ color: '#28a745', fontWeight: 'bold', marginBottom: '10px' }}>
                    <i className="bi bi-check-circle-fill me-2"></i>
                    {successPart}
                </div>

                {/* Error Message in Red */}
                {errorHeader && (
                    <div style={{ color: '#dc3545', fontSize: '0.95em', borderTop: '1px solid #e9ecef', paddingTop: '10px' }}>
                        <strong>{errorHeader}</strong>
                        <div style={{ marginTop: '5px', paddingLeft: '10px' }}>
                            {errorList.map((err, index) => (
                                <div key={index} style={{ marginBottom: '2px' }}>
                                    {err}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div style={styles.container}>
            <h2 style={styles.heading}>
                Upload Beneficiary List for Project {projectId}
            </h2>

            <div style={styles.templateSection}>
                <div style={styles.templateInfo}>
                    <FaInfoCircle style={{ marginRight: '8px', color: '#0056b3' }} />
                    <span>Please upload data using the standard format.</span>
                </div>
                <a 
                    href="/beneficiary_template.xlsx" 
                    download="Beneficiary_Template.xlsx" 
                    style={styles.downloadLink}
                >
                    <FaFileDownload style={{ marginRight: '8px' }} />
                    Download Sample Excel Template
                </a>
            </div>

            <form className="form-grid" onSubmit={handleSubmit} onReset={handleCancel}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                    <div>
                        <label style={styles.label}>Voucher Points</label>
                        <input
                            type="number"
                            style={styles.input}
                            value={voucherPoints}
                            onChange={(e) => setVoucherPoints(e.target.value)}
                            required
                            min="1"
                            placeholder="e.g. 500"
                        />
                    </div>
                    <div></div>
                    
                    <div>
                        <label style={styles.label}>Validity Start Date</label>
                        <input
                            type="date"
                            style={styles.input}
                            value={validityStart}
                            onChange={(e) => setValidityStart(e.target.value)}
                            required
                        />
                    </div>
                    <div>
                        <label style={styles.label}>Validity End Date</label>
                        <input
                            type="date"
                            style={styles.input}
                            value={validityEnd}
                            onChange={(e) => setValidityEnd(e.target.value)}
                            required
                            min={validityStart}
                        />
                    </div>
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                    <label htmlFor="beneficiary_files" style={styles.label}>
                        Select Beneficiary List File(s)
                    </label>
                    <input
                        type="file"
                        id="beneficiary_files"
                        accept=".xls,.xlsx"
                        multiple
                        required
                        onChange={handleFileChange}
                        disabled={isLoading || !projectId || !departmentId}
                        style={{ padding: '10px 0' }}
                    />
                </div>

                {isLoading && (
                    <p style={{ color: "#007bff", marginTop: "15px" }}>
                        Uploading, please wait...
                    </p>
                )}
                
                {error && <p style={{ color: "red", marginTop: "15px" }}>{error}</p>}
                
                {/* Result Message (Success + List of Failures) */}
                {renderStatusMessage()}

                <div style={styles.buttonGroup}>
                    <button
                        type="submit"
                        style={styles.btnPrimary}
                        disabled={isLoading || !projectId || !departmentId}
                    >
                        {isLoading ? "Uploading..." : "Submit"}
                    </button>
                    <button type="reset" style={styles.btnSecondary} disabled={isLoading}>
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default UploadBeneficiaryListPage;

const styles = {
    container: {
        backgroundColor: "#ffffff",
        padding: "30px",
        maxWidth: "750px",
        margin: "40px auto",
        borderRadius: "8px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        fontFamily: "Arial, sans-serif",
        color: "#343a40",
    },
    heading: {
        textAlign: "center",
        color: "#007bff",
        marginBottom: "24px",
    },
    templateSection: {
        backgroundColor: "#eef6fc",
        border: "1px solid #cce5ff",
        borderRadius: "6px",
        padding: "15px",
        marginBottom: "25px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "10px"
    },
    templateInfo: {
        display: "flex",
        alignItems: "center",
        color: "#004085",
        fontSize: "0.95em"
    },
    downloadLink: {
        display: "flex",
        alignItems: "center",
        textDecoration: "none",
        backgroundColor: "#fff",
        color: "#007bff",
        padding: "8px 15px",
        borderRadius: "4px",
        border: "1px solid #007bff",
        fontWeight: "bold",
        fontSize: "0.9em",
        transition: "background-color 0.2s"
    },
    label: {
        display: "block",
        marginBottom: "8px",
        fontWeight: "bold",
        color: "#495057"
    },
    input: {
        width: "100%",
        padding: "10px",
        borderRadius: "4px",
        border: "1px solid #ced4da",
        boxSizing: "border-box"
    },
    buttonGroup: {
        display: "flex",
        justifyContent: "flex-end",
        gap: "10px",
        marginTop: "24px",
    },
    btnPrimary: {
        padding: "10px 20px",
        backgroundColor: "#007bff",
        color: "white",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        fontWeight: "bold",
    },
    btnSecondary: {
        padding: "10px 20px",
        backgroundColor: "#6c757d",
        color: "white",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        fontWeight: "bold",
    },
};